import { Link } from "react-router-dom";
import { ArrowRight, Mail, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CTABand from "@/components/CTABand";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const FAQS = [
  {
    q: "Who is RentAnything.ai for?",
    a: "Owners and operators of rental businesses — marine, equipment, recreation, powersports, tours, and event rentals. If your business rents something to someone for a defined period, we cover the workflow.",
  },
  {
    q: "What payment processors do you support?",
    a: "We integrate with Stripe out of the box for cards, ACH, and net-terms accounts. Existing merchant accounts can be wired in during onboarding for most operators.",
  },
  {
    q: "How long does onboarding take?",
    a: "Most operators are live within 30 days. Single-location businesses with under 50 SKUs can be operational in under a week. Migration from an existing platform is included.",
  },
  {
    q: "What are your contract terms?",
    a: "Month-to-month after a short paid pilot. No multi-year lock-in. We earn the renewal each season.",
  },
  {
    q: "I'm switching from another platform — what does migration look like?",
    a: "We import your existing inventory, customer database, future reservations, and historical bookings. You'll have a dedicated migration owner from day one of the pilot.",
  },
  {
    q: "What's your refund policy?",
    a: "If RentAnything isn't a fit during the pilot, you don't pay. After go-live, the month-to-month model means you can step out whenever — though almost no one does.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Pricing() {
  useSeo({
    title: "Pricing · Contact us for introductory pricing",
    description:
      "Special introductory pricing for early operators. Book a quick demo to see RentAnything configured for your fleet and get a custom quote.",
    path: "/pricing",
    jsonLd: FAQ_JSONLD,
  });
  useReveal();

  return (
    <>
      <section className="ra-mesh">
        <div className="ra-container py-20 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="ra-pill mx-auto">
              <span className="h-1.5 w-1.5 rounded-full bg-ra-amber" />
              Limited-time launch pricing
            </span>
            <h1 className="mt-5 ra-h1" data-testid="pricing-headline">
              Contact us for special introductory pricing.
            </h1>
            <p className="mt-5 ra-lead">
              We're onboarding launch-cohort operators with custom packages tied to fleet size, not a published tier matrix. Tell us what you rent — we'll get you a number same-day.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-2xl reveal">
            <div className="rounded-3xl border border-ra-line bg-white p-8 sm:p-10 shadow-card">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ra-ocean/10 text-ra-ocean">
                  <Mail size={22} />
                </div>
                <h2 className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-ra-ink">Get a custom quote</h2>
                <p className="mt-3 max-w-md text-sm sm:text-base text-ra-ink/70">
                  30-minute demo with an operator (not a sales rep). We'll configure RentAnything for your fleet live, then send you pricing the same day.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                  <Link to="/contact" className="ra-btn-primary" data-testid="pricing-book-demo-btn">
                    Book a Demo <ArrowRight size={16} />
                  </Link>
                  <a href="mailto:inquiry@rentanything.ai" className="ra-btn-ghost" data-testid="pricing-email-btn">
                    inquiry@rentanything.ai
                  </a>
                </div>
                <ul className="mt-8 grid grid-cols-1 gap-3 text-sm text-ra-ink/75 sm:grid-cols-3">
                  {["No annual lock-in", "Migration included", "Pilot before pay"].map((t) => (
                    <li key={t} className="flex items-center justify-center gap-2">
                      <CheckCircle2 size={14} className="text-ra-ocean" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ra-section">
        <div className="ra-container max-w-3xl">
          <div className="text-center reveal">
            <span className="ra-eyebrow">Pricing FAQ</span>
            <h2 className="mt-3 ra-h2">Honest answers, no sales fog.</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 w-full" data-testid="pricing-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-ra-line">
                <AccordionTrigger className="text-left text-base font-semibold text-ra-ink hover:text-ra-ocean">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-ra-ink/75">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABand />
    </>
  );
}
